import { motion, AnimatePresence } from 'motion/react'
import { Calendar, Check } from 'lucide-react'
import { useTelegram } from '@/shared/lib/telegram'
import { cn } from '@/shared/lib/utils'

export interface EventActionBarProps {
  /**
   * Цена события
   */
  price: number | null
  /**
   * Событие бесплатное
   */
  isFree?: boolean
  /**
   * Заявка отправлена
   */
  isApplied?: boolean
  /**
   * Состояние загрузки
   */
  loading?: boolean
  /**
   * Дизейбл кнопки (например, нет мест)
   */
  disabled?: boolean
  /**
   * Причина дизейбла: 'full' | 'past' | 'cancelled'
   */
  disabledReason?: 'full' | 'past' | 'cancelled'
  /**
   * Обработчик записи на событие
   */
  onApply: () => void
}

/**
 * Виджет: Фиксированный CTA-блок снизу экрана для события
 * 
 * Показывается в 100% случаев, фиксированно на нижней части экрана.
 * При записи переходит в состояние "Заявка отправлена".
 */
export function EventActionBar({
  price,
  isFree = false,
  isApplied = false,
  loading = false,
  disabled = false,
  disabledReason,
  onApply,
}: EventActionBarProps) {
  const { hapticFeedback } = useTelegram()
  
  // Текст для дизейбленной кнопки
  const getDisabledText = () => {
    switch (disabledReason) {
      case 'past': return 'Событие прошло'
      case 'cancelled': return 'Отменено'
      case 'full': return 'Мест нет'
      default: return 'Мест нет'
    }
  }
  
  const handleApply = () => {
    hapticFeedback.impactOccurred('medium')
    onApply()
  }
  
  // Форматируем отображение цены
  const priceText = isFree ? 'Бесплатно' : (price && price > 0 ? `${price} ₽` : 'Уточняется')
  const priceColorClass = isFree 
    ? 'text-success dark:text-success-light' 
    : 'text-primary dark:text-primary-light'
  
  return (
    <div 
      className="fixed left-0 right-0 z-50 border-t border-silk bg-paper dark:border-dark-border dark:bg-[#0F1113] [bottom:var(--bottom-nav-height,0px)] md:bottom-0"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div className="px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Информация о цене */}
          <div className="flex-1 min-w-0">
            <div className={`text-lg font-bold ${priceColorClass}`}>
              {priceText}
            </div>
          </div>
          
          {/* Кнопка записи */}
          <AnimatePresence mode="wait">
            {isApplied ? (
              <motion.div
                key="applied"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="flex items-center gap-2 px-6 py-3 bg-success dark:bg-success/90 text-white rounded-2xl font-medium"
              >
                <Check className="h-5 w-5" />
                <span>Заявка отправлена</span>
              </motion.div>
            ) : (
              <motion.button
                key="apply"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={handleApply}
                disabled={loading || disabled}
                className={cn(
                  'px-8 py-3 bg-primary dark:bg-primary-dark text-white rounded-2xl font-medium',
                  'hover:bg-primary-dark dark:hover:bg-primary-darker active:scale-95 transition-all',
                  'touch-target disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed',
                  'flex items-center gap-2'
                )}
              >
                <Calendar className="h-5 w-5" />
                <span>{loading ? 'Отправка...' : disabled ? getDisabledText() : 'Записаться'}</span>
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}




